import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import './ResetPasswordPage.css';

const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [tokenValid, setTokenValid] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const { resetToken } = useParams();
  const navigate = useNavigate();

  // Password validation
  const validatePassword = (password) => {
    const minLength = password.length >= 6;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    
    return {
      isValid: minLength && hasUpperCase && hasLowerCase && hasNumber,
      minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumber
    };
  };

  const passwordValidation = validatePassword(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match. Please try again.');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (!passwordValidation.isValid) {
      setError('Password must be at least 6 characters and contain at least one uppercase letter, one lowercase letter, and one number.');
      setLoading(false);
      return;
    }

    try {
      const response = await authService.resetPassword(resetToken, password);
      
      if (response.success) {
        setMessage(response.message || 'Password reset successfully!');
        
        // Store new token if provided
        if (response.token) {
          localStorage.setItem('authToken', response.token);
        }
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        if (response.error === 'Invalid or expired token') {
          setTokenValid(false);
        }
        setError(response.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-container">
          <div className="invalid-token-section">
            <div className="invalid-token-icon">⚠️</div>
            <h2>Invalid Reset Link</h2>
            <p>The password reset link is invalid or has expired.</p>
            <button 
              onClick={() => navigate('/forgot-password')}
              className="request-new-link-btn"
            >
              Request New Reset Link
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <div className="reset-password-header">
          <h2>🔐 Reset Password</h2>
          <p>Enter your new password below to complete the reset process.</p>
        </div>
        
        <form onSubmit={handleSubmit} className="reset-password-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter new password"
                className="form-input"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "🙈" : "👁️"}
              </button>
            </div>
            
            {/* Password strength indicator */}
            <div className="password-strength">
              <div className="strength-bar">
                <div 
                  className={`strength-fill ${passwordValidation.isValid ? 'strong' : password.length > 0 ? 'weak' : ''}`}
                  style={{ width: `${Math.min((password.length / 6) * 100, 100)}%` }}
                ></div>
              </div>
              <div className="strength-requirements">
                <span className={passwordValidation.minLength ? 'valid' : 'invalid'}>
                  ✓ At least 6 characters
                </span>
                <span className={passwordValidation.hasUpperCase ? 'valid' : 'invalid'}>
                  ✓ One uppercase letter
                </span>
                <span className={passwordValidation.hasLowerCase ? 'valid' : 'invalid'}>
                  ✓ One lowercase letter
                </span>
                <span className={passwordValidation.hasNumber ? 'valid' : 'invalid'}>
                  ✓ One number
                </span>
              </div>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm new password"
                className="form-input"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? "🙈" : "👁️"}
              </button>
            </div>
            
            {confirmPassword && password !== confirmPassword && (
              <div className="password-match-error">
                Passwords do not match
              </div>
            )}
          </div>
          
          <button 
            type="submit" 
            disabled={loading || !password || !confirmPassword || !passwordValidation.isValid || password !== confirmPassword}
            className="submit-btn"
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                Resetting Password...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {message && (
          <div className="success-message">
            <span className="success-icon">✅</span>
            <span>{message}</span>
            <p className="redirect-message">Redirecting to login page...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage; 